import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, type GamesResponse } from "../api";
import { useClubs } from "../ClubContext";
import { GameCard } from "../components/GameCard";
import { ALL_GENDERS, genderLabel } from "../lib/gameFormat";

export function GamesPage() {
  const navigate = useNavigate();
  const { activeClub, loading: clubsLoading } = useClubs();
  const [games, setGames] = useState<GamesResponse["games"] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Filters. Empty string = "no filter applied"; matches our select's
  // sentinel option value.
  const [ageFilter, setAgeFilter] = useState<string>("");
  const [genderFilter, setGenderFilter] = useState<string>("");

  const clubId = activeClub?.clubId;

  useEffect(() => {
    if (!clubId) return;
    let cancelled = false;
    // Clear immediately so a club switch shows "Loading…" rather than the
    // previous club's games until the new list lands.
    setGames(null);
    (async () => {
      try {
        const res = await api<GamesResponse>(`/v1/games?clubId=${encodeURIComponent(clubId)}`);
        if (cancelled) return;
        setGames(res.games);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "Failed to load";
        setError(msg);
        if (msg.toLowerCase().includes("unauthor")) navigate("/login", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clubId, navigate]);

  // Age-group dropdown options are the distinct codes present in the
  // loaded games. Sorted alphabetically; nulls excluded.
  const ageOptions = useMemo(() => {
    if (!games) return [];
    const set = new Set<string>();
    for (const g of games) {
      if (g.ageGroupCode) set.add(g.ageGroupCode);
    }
    return Array.from(set).sort();
  }, [games]);

  const filtered = useMemo(() => {
    if (!games) return null;
    return games.filter((g) => {
      if (ageFilter && g.ageGroupCode !== ageFilter) return false;
      if (genderFilter && (g.gender ?? "").toLowerCase() !== genderFilter) return false;
      return true;
    });
  }, [games, ageFilter, genderFilter]);

  return (
    <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold">Games</h2>
          {games && games.length > 0 && (
            <div className="flex items-center gap-2">
              <select
                value={ageFilter}
                onChange={(e) => setAgeFilter(e.target.value)}
                className="rounded-md border border-slate-300 px-2 py-1 text-sm"
                aria-label="Filter by age group"
              >
                <option value="">All ages</option>
                {ageOptions.map((ag) => (
                  <option key={ag} value={ag}>{ag}</option>
                ))}
              </select>
              <select
                value={genderFilter}
                onChange={(e) => setGenderFilter(e.target.value)}
                className="rounded-md border border-slate-300 px-2 py-1 text-sm"
                aria-label="Filter by gender"
              >
                <option value="">All genders</option>
                {ALL_GENDERS.map((g) => (
                  <option key={g} value={g}>{genderLabel(g)}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {error && <p className="mb-4 text-sm text-rose-600">{error}</p>}
        {!clubsLoading && !activeClub && !error && (
          <p className="text-sm text-slate-500">You don't belong to any club yet.</p>
        )}
        {filtered === null && !error && (clubsLoading || activeClub) && (
          <p className="text-sm text-slate-500">Loading…</p>
        )}
        {filtered && filtered.length === 0 && (
          <p className="text-sm text-slate-500">
            {games && games.length === 0
              ? "No games yet. Complete one on the iPad app."
              : "No games match the current filters."}
          </p>
        )}
        {filtered && filtered.length > 0 && (
          <div className="space-y-3">
            {filtered.map((g) => (
              <Link key={g.id} to={`/games/${g.id}`} className="block">
                <GameCard game={g} />
              </Link>
            ))}
          </div>
        )}
    </main>
  );
}
